import React from "react";
import {
	Input,
	Button,
	makeStyles,
	tokens,
	Label,
	Tooltip,
} from "@fluentui/react-components";
import { Search20Regular, LockClosed16Regular } from "@fluentui/react-icons";
import { useSubscription } from "../context/SubscriptionContext";
import { useAuth } from "../context/BaseAuthProvider";
import { useEditor } from "../editor/EditorContext";
import PaywallModal from "./PaywallModal";

const useStyles = makeStyles({
	root: {
		display: "flex",
		flexDirection: "column",
		gap: tokens.spacingVerticalS,
		width: "100%",
	},
	searchInput: {
		width: "100%",
	},
	optionsContainer: {
		display: "flex",
		flexDirection: "column",
		gap: tokens.spacingVerticalXS,
		overflowY: "auto",
		paddingRight: tokens.spacingHorizontalXS,
	},
	optionButton: {
		width: "100%",
		justifyContent: "flex-start",
		textAlign: "left",
	},
	noResults: {
		padding: tokens.spacingVerticalM,
		color: tokens.colorNeutralForeground3,
		textAlign: "center",
	},
	lockedButton: {
		opacity: 0.85,
	},
	lockIcon: {
		marginLeft: tokens.spacingHorizontalXS,
	},
});

export interface SearchOption {
	id: string;
	text: string;
}

interface SearchAndInsertProps {
	label: string;
	options: SearchOption[];
	placeholder?: string;
	disabled?: boolean;
	/** Optional override for the insert action. Defaults to the host editor's insertText. */
	onAction?: (text: string) => Promise<void>;
	/** Show a "Copy" tooltip on each option button (for web use). */
	showCopyTooltip?: boolean;
	/** Called after a successful insert, used to signal first-insert to guidance system. */
	onInsert?: () => void;
}

export const SearchAndInsert: React.FC<SearchAndInsertProps> = ({
	label,
	options,
	placeholder = "Search...",
	disabled = false,
	onAction,
	showCopyTooltip = false,
	onInsert,
}) => {
	const styles = useStyles();
	const [searchValue, setSearchValue] = React.useState("");
	const [isInserting, setIsInserting] = React.useState(false);
	const [showPaywall, setShowPaywall] = React.useState(false);
	const { canInsert, openCheckout } = useSubscription();
	const { isAuthenticated, login } = useAuth();
	const editor = useEditor();

	const filteredOptions = React.useMemo(() => {
		if (!searchValue) return options;
		const lowerSearch = searchValue.toLowerCase();
		return options.filter((opt) =>
			opt.text.toLowerCase().includes(lowerSearch),
		);
	}, [options, searchValue]);

	const insertText = async (text: string) => {
		setIsInserting(true);
		try {
			if (onAction) {
				await onAction(text);
			} else {
				await editor.insertText(text);
			}
			onInsert?.();
		} catch (error) {
			console.error("Error with text action:", error);
		} finally {
			setIsInserting(false);
		}
	};

	const handleButtonClick = async (text: string) => {
		// Check subscription status before inserting
		if (!canInsert) {
			setShowPaywall(true);
			return;
		}
		await insertText(text);
	};

	const handleClosePaywall = () => {
		setShowPaywall(false);
	};

	const handleSubscribe = (priceTier: 'annual' | 'monthly') => {
		setShowPaywall(false);
		openCheckout(priceTier);
	};

	return (
		<div className={styles.root}>
			<Label>{label}</Label>
			<Input
				className={styles.searchInput}
				placeholder={placeholder}
				value={searchValue}
				onChange={(_e, data) => setSearchValue(data.value)}
				contentBefore={<Search20Regular />}
				disabled={disabled || isInserting}
			/>
			<div className={styles.optionsContainer}>
				{filteredOptions.length > 0 ? (
					filteredOptions.map((option) => (
						<Tooltip
							key={option.id}
							content="Copy"
							relationship="label"
							hideDelay={0}
							withArrow
							visible={showCopyTooltip ? undefined : false}
						>
							<Button
								className={styles.optionButton}
								onClick={() => handleButtonClick(option.text)}
								disabled={disabled || isInserting}
							>
								{option.text}
							</Button>
						</Tooltip>
					))
				) : (
					<div className={styles.noResults}>
						{searchValue ? "No results found" : "No options available"}
					</div>
				)}
			</div>
			<PaywallModal
				isOpen={showPaywall}
				isAuthenticated={isAuthenticated}
				onClose={handleClosePaywall}
				onSubscribe={handleSubscribe}
				onLogin={() => login('microsoft')}
				onLoginGoogle={() => login('google')}
			/>
		</div>
	);
};

export default SearchAndInsert;
